// kfe-settings.js — settings screen.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/SettingsPanel.cs
// (and the iOS KFEUserSettingViewController + KFEUserSettingViewModel)
//
// V1 row set (per Brief 014):
//   - Language switcher          (en / 中文)
//   - Auto-continue toggle
//   - Auto-repeat toggle
//   - Personal Promise           (link → kfe-personal-promise; Brief 013)
//   - Study path                 (placeholder until Brief 024 lands)
//   - Manage account             (external link)
//   - Sign out                   (server logout + clears session, routes
//                                 to /login; Brief 191)
//   - App version                (read-only, build-time injected)
//
// Dev-only rows (kept from the previous stub) — visible when localStorage
// `kfe_dev_mode` is set. Entered by visiting the app once with `?dev=1`:
//   - Gating toggle
//   - Disable dev mode
//
// Toggle/select changes go through services.learner.updateSetting(), which
// persists to IndexedDB AND dispatches a `kfe:setting-changed` document
// event. Brief 025's sync engine consumes that event later; for now the
// dispatch is a no-op observer hook.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, setLanguage, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { signOutFull } from "../data/auth-store.js";
import { VERSION } from "../version.js";
import * as router from "../router.js";
import { getActivePathwayId } from "../core/active-pathway.js";
import { loadPathway } from "../data/pathway-loader.js";

// TODO(chris): confirm the canonical Manage Account URL. Brief 014
// references kungfuenglish.com/my-account; replace if different.
const MANAGE_ACCOUNT_URL = "https://kungfuenglish.com/my-account";

export class KFESettings extends KFEComponent {
  static styles = `
    :host { display: block; }
    .row {
      display: flex; justify-content: space-between; align-items: center;
      gap: 1rem;
      padding: 0.75rem 0; border-bottom: 1px solid var(--kfe-border);
    }
    .row .label { display: flex; flex-direction: column; gap: 0.125rem; }
    .row .label .sub, .row .state {
      font-size: 0.8rem; color: var(--kfe-muted);
    }
    .row a { color: var(--kfe-link); text-decoration: none; }
    .row .value { color: var(--kfe-muted); font-variant-numeric: tabular-nums; }
    .row .chevron { color: var(--kfe-muted); }
    .row.clickable { cursor: pointer; }
    .row.clickable:hover { background: var(--kfe-surface-hover); }
    select, input[type="checkbox"] { font: inherit; }
    button.link {
      background: none; border: none; color: var(--kfe-link); cursor: pointer;
      font: inherit; padding: 0.25rem 0.5rem;
    }
    .section-header {
      margin: 1rem 0 0.5rem; font-size: 0.8rem; color: var(--kfe-muted);
      text-transform: uppercase; letter-spacing: 0.05em;
    }
    .toast {
      position: fixed; left: 50%; bottom: 4rem; transform: translateX(-50%);
      background: var(--kfe-surface); color: var(--kfe-fg);
      padding: 0.5rem 1rem; border-radius: var(--kfe-radius);
      border: 1px solid var(--kfe-border);
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      pointer-events: none; opacity: 0; transition: opacity 0.15s ease;
      z-index: 10;
    }
    .toast[data-shown="true"] { opacity: 1; }
  `;

  /** @type {boolean} */ _autoContinue = true;
  /** @type {boolean} */ _autoRepeat = true;
  /** @type {boolean} */ _gating = true;
  /** @type {boolean} */ _devMode = false;
  /** @type {boolean} */ _promiseSigned = false;
  /** @type {number | null} */ _promiseSignedAt = null;
  /** @type {ReturnType<typeof setTimeout> | null} */ _toastTimer = null;
  /** @type {string} Current pathway's title (per UI language); "" until loaded. */ _pathwayTitle = "";

  async onConnected() {
    const [autoContinue, autoRepeat, gating, signed, signedAt] = await Promise.all([
      services.learner.getSetting("auto_continue"),
      services.learner.getSetting("auto_repeat"),
      services.learner.getSetting("gating_enabled"),
      services.learner.getSetting("personal_promise_signed"),
      services.learner.getSetting("personal_promise_signed_at"),
    ]);
    // Defaults: toggles default ON if never set.
    this._autoContinue = autoContinue !== false;
    this._autoRepeat = autoRepeat !== false;
    this._gating = gating !== false;
    this._promiseSigned = signed === true;
    this._promiseSignedAt = typeof signedAt === "number" ? signedAt : null;
    this._devMode = localStorage.getItem("kfe_dev_mode") === "true";

    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title: t("settings.title") },
      bubbles: true,
      composed: true,
    }));

    // If another part of the app changes a setting (e.g. the personal
    // promise screen signs the commitment), refresh the relevant row
    // without requiring a navigation cycle.
    const onSettingChanged = () => { this._refreshPromise(); };
    document.addEventListener("kfe:setting-changed", onSettingChanged);
    this.addCleanup(() => document.removeEventListener("kfe:setting-changed", onSettingChanged));

    // Pathway row sub-label = the active pathway's title (Brief 157). Resolved
    // off the critical render so the rest of Settings paints immediately. Also
    // refresh if the active pathway changes while Settings is mounted.
    this._refreshPathwayTitle();
    const onPathwayChanged = () => { this._refreshPathwayTitle(); };
    document.addEventListener("kfe:pathway-changed", onPathwayChanged);
    this.addCleanup(() => document.removeEventListener("kfe:pathway-changed", onPathwayChanged));

    this._scheduleRender();
  }

  onDisconnected() {
    if (this._toastTimer) {
      clearTimeout(this._toastTimer);
      this._toastTimer = null;
    }
  }

  async _refreshPathwayTitle() {
    const id = getActivePathwayId();
    try {
      const pw = await loadPathway(id);
      this._pathwayTitle = (getLanguage() === "zh-Hans" && pw.titleZh)
        ? pw.titleZh
        : (pw.titleEn || id);
    } catch {
      this._pathwayTitle = id;
    }
    this._scheduleRender();
  }

  async _refreshPromise() {
    const [signed, signedAt] = await Promise.all([
      services.learner.getSetting("personal_promise_signed"),
      services.learner.getSetting("personal_promise_signed_at"),
    ]);
    this._promiseSigned = signed === true;
    this._promiseSignedAt = typeof signedAt === "number" ? signedAt : null;
    this._scheduleRender();
  }

  async _setLang(/** @type {Event} */ e) {
    const v = /** @type {HTMLSelectElement} */ (e.target).value;
    await setLanguage(v);
    await services.learner.updateSetting("language", v);
    this._scheduleRender();
  }

  async _setAutoContinue(/** @type {Event} */ e) {
    const v = /** @type {HTMLInputElement} */ (e.target).checked;
    this._autoContinue = v;
    await services.learner.updateSetting("auto_continue", v);
  }

  async _setAutoRepeat(/** @type {Event} */ e) {
    const v = /** @type {HTMLInputElement} */ (e.target).checked;
    this._autoRepeat = v;
    await services.learner.updateSetting("auto_repeat", v);
  }

  async _setGating(/** @type {Event} */ e) {
    const v = /** @type {HTMLInputElement} */ (e.target).checked;
    this._gating = v;
    await services.learner.updateSetting("gating_enabled", v);
  }

  _disableDevMode = () => {
    localStorage.removeItem("kfe_dev_mode");
    this._devMode = false;
    document.dispatchEvent(new Event("kfe:dev-mode-changed"));
    this._scheduleRender();
  };

  _openPathwaySwitcher = () => {
    router.navigate("/settings/pathway");
  };

  // Brief 191: server-side invalidation first (signOutFull awaits crmLogout
  // before clearing the local session), then route. No spinner — the local
  // clear + route fire whatever the server does, so a slow or failed logout
  // can never strand the learner signed-in.
  _signOut = async () => {
    await signOutFull();
    router.navigate("/login");
  };

  /** @param {string} message */
  _showToast(message) {
    const toast = /** @type {HTMLElement | null} */ (
      this.shadowRoot?.querySelector(".toast")
    );
    if (!toast) return;
    toast.textContent = message;
    toast.dataset.shown = "true";
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.dataset.shown = "false";
      this._toastTimer = null;
    }, 1800);
  }

  /** @param {number} ts */
  _formatPromiseDate(ts) {
    return new Intl.DateTimeFormat(getLanguage(), {
      year: "numeric", month: "short", day: "numeric",
    }).format(new Date(ts));
  }

  render() {
    const lang = getLanguage();
    const promiseState = this._promiseSigned && this._promiseSignedAt != null
      ? t("settings.personalPromise.signed", { date: this._formatPromiseDate(this._promiseSignedAt) })
      : t("settings.personalPromise.unsigned");
    return html`
      <div class="row">
        <label class="label" for="lang">${t("settings.language")}</label>
        <select id="lang" .value=${lang} aria-label="${t("settings.language")}" on:change=${(e) => this._setLang(e)}>
          <option value="en">English</option>
          <option value="zh-Hans">中文</option>
        </select>
      </div>

      <div class="row">
        <label class="label" for="auto-continue">${t("settings.autoContinue")}</label>
        <input id="auto-continue" type="checkbox"
               .checked=${!!this._autoContinue}
               on:change=${(e) => this._setAutoContinue(e)} />
      </div>

      <div class="row">
        <label class="label" for="auto-repeat">${t("settings.autoRepeat")}</label>
        <input id="auto-repeat" type="checkbox"
               .checked=${!!this._autoRepeat}
               on:change=${(e) => this._setAutoRepeat(e)} />
      </div>

      <div class="row">
        <a href="/personal-promise">${t("settings.personalPromise")}</a>
        <span class="state">${promiseState}</span>
      </div>

      <div class="row clickable" role="button" tabindex="0" on:click=${this._openPathwaySwitcher}>
        <span class="label">
          <span>${t("settings.pathway")}</span>
          <span class="sub">${this._pathwayTitle || t("loading")}</span>
        </span>
        <span class="chevron" aria-hidden="true">›</span>
      </div>

      <div class="row">
        <span class="label">${t("settings.manageAccount")}</span>
        <a href="${MANAGE_ACCOUNT_URL}" target="_blank" rel="noopener noreferrer">
          ${t("settings.manageAccount")} ↗
        </a>
      </div>

      <div class="row">
        <span class="label">${t("settings.signOut")}</span>
        <button class="link" type="button" on:click=${this._signOut}>${t("settings.signOut")}</button>
      </div>

      <div class="row">
        <span class="label">${t("settings.appVersion")}</span>
        <span class="value">v${VERSION}</span>
      </div>

      ${this._devMode ? html`
        <div class="section-header">${t("settings.devMode")}</div>
        <div class="row">
          <label class="label" for="gating">${t("settings.gating")}</label>
          <input id="gating" type="checkbox"
                 .checked=${!!this._gating}
                 on:change=${(e) => this._setGating(e)} />
        </div>
        <div class="row">
          <span class="label">${t("settings.devMode")}</span>
          <button class="link" type="button" on:click=${this._disableDevMode}>${t("settings.disableDevMode")}</button>
        </div>
      ` : ""}

      <div class="toast" role="status" aria-live="polite" data-shown="false"></div>
    `;
  }
}
